import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { API, FALLBACK_ARTICLES, faqs as FALLBACK_FAQS } from "./site/data";
import { Navbar } from "./site/Navbar";
import { Hero } from "./site/Hero";
import { Marquee, PatientTicker } from "./site/Marquee";
import { VisitSection } from "./site/VisitSection";
import { ClinicNotes } from "./site/ClinicNotes";
import { AboutSection } from "./site/AboutSection";
import { ManifestoStrip } from "./site/ManifestoStrip";
import { SpecialtiesSection } from "./site/SpecialtiesSection";
import { ArticlesSection } from "./site/ArticlesSection";
import { FaqSection } from "./site/FaqSection";
import { AcademicSection } from "./site/AcademicSection";
import { GallerySection } from "./site/GallerySection";
import { FooterSection } from "./site/FooterSection";
import { AppointmentModal, ArticleModal, GalleryLightbox } from "./site/Modals";

export default function Home() {
  const [showForm, setShowForm] = useState(false);
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);
  const [updates, setUpdates] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [posts, setPosts] = useState([]);
  const [faqs, setFaqs] = useState(FALLBACK_FAQS);
  const [openArticle, setOpenArticle] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let frame;
    const raf = (time) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);

  useEffect(() => {
    if (lenisRef.current) { (showForm || openArticle || lightboxIndex !== null) ? lenisRef.current.stop() : lenisRef.current.start(); }
  }, [showForm, openArticle, lightboxIndex]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("book") === "1") setShowForm(true);
  }, []);

  useEffect(() => {
    axios.get(`${API}/articles`).then(({ data }) => { if (data.length) setArticles(data); }).catch(() => {});
    axios.get(`${API}/updates`).then(({ data }) => setUpdates(data)).catch(() => {});
    axios.get(`${API}/gallery`).then(({ data }) => setGallery(data)).catch(() => {});
    axios.get(`${API}/posts`).then(({ data }) => setPosts(data)).catch(() => {});
    axios.get(`${API}/faqs`).then(({ data }) => { if (data.length) setFaqs(data.map((f) => [f.question, f.answer])); }).catch(() => {});
  }, []);

  const scrollTo = (id) => {
    if (lenisRef.current) { lenisRef.current.scrollTo(`#${id}`, { offset: -10 }); }
    else { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }
  };

  const openBooking = () => setShowForm(true);

  return (
    <div className="site-shell">
      <Toaster position="top-center" />
      <Navbar scrollTo={scrollTo} onBook={openBooking} hasGallery={gallery.length > 0} hasPosts={posts.length > 0} />
      <main id="home">
        <Hero onBook={openBooking} />
        <Marquee />
        <VisitSection onBook={openBooking} />
        <ClinicNotes updates={updates} />
        <AboutSection onBook={openBooking} />
        <PatientTicker />
        <ManifestoStrip />
        <SpecialtiesSection />
        <ArticlesSection articles={articles} onOpenArticle={setOpenArticle} />
        <FaqSection faqs={faqs} />
        <AcademicSection />
        <GallerySection gallery={gallery} onOpenLightbox={setLightboxIndex} />
      </main>
      <FooterSection onBook={openBooking} />
      {lightboxIndex !== null && gallery[lightboxIndex] && (
        <GalleryLightbox
          item={gallery[lightboxIndex]}
          index={lightboxIndex}
          total={gallery.length}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
      {showForm && <AppointmentModal onClose={() => setShowForm(false)} />}
      {openArticle && <ArticleModal article={openArticle} onClose={() => setOpenArticle(null)} onBook={openBooking} />}
    </div>
  );
}
